import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, AlertCircle, LogIn, UserPlus, Loader2 } from 'lucide-react';
import {
  lookupBuildingByJoinCode, fetchAccount,
  type PublicBuildingSummary,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
import { SwitchBuildingModal } from '@/components/ui/SwitchBuildingModal';
import formStyles from '@/components/Form.module.css';
import styles from './JoinPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * /join?code=K3X9PW landing page (Session 8.c).
 *
 * Public-facing — works for both authenticated and unauthenticated
 * visitors. Forks based on auth state:
 *   - Logged-out: two CTAs ("I have an account" → /login with the
 *     code threaded through; "Create an account" → /register with
 *     the code threaded through).
 *   - Logged-in: opens the SwitchBuildingModal directly so the
 *     switch flow runs inline; on successful switch the modal
 *     handles the relogin redirect.
 *
 * The page does its own lookup against /tenants/by-join-code so
 * it can render the building name in the headline before the user
 * decides which fork to take.
 */
export function JoinPage({ onNavigate }: Props) {
  const { t } = useTranslation('authFlows');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.user);
  // Fetch the user's current tenant info ONLY when authenticated.
  // The modal reads is_default to decide first-join vs switch
  // framing.
  const accountQ = useQuery({
    queryKey: ['account'],
    queryFn: fetchAccount,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const [code, setCode] = useState<string | null>(null);
  const [building, setBuilding] = useState<PublicBuildingSummary | null>(null);
  const [phase, setPhase] = useState<'looking' | 'found' | 'invalid'>('looking');
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  // Pull the code off the URL once on mount. The App router uses
  // pushState navigation; window.location.search is the source of
  // truth for query params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('code') ?? '';
    const normalized = raw.trim().toUpperCase();
    if (!normalized || normalized.length !== 6) {
      setPhase('invalid');
      return;
    }
    setCode(normalized);
    // Unified lookup: Location-first, Tenant-second. Cross-collection
    // codegen on the server guarantees the code resolves to at most
    // one entity, so the order matters only on 404.
    void lookupBuildingByJoinCode(normalized).then((result) => {
      if (result) {
        setBuilding(result);
        setPhase('found');
      } else {
        setPhase('invalid');
      }
    });
  }, []);

  if (phase === 'looking') {
    return (
      <AuthLayout title={t('join.lookingTitle')} subtitle={t('join.lookingSubtitle')}>
        <div className={styles.spinnerRow}>
          <Loader2 size={24} className={styles.spin} />
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'invalid' || !building) {
    return (
      <AuthLayout
        title={t('join.notFoundTitle')}
        subtitle={t('join.notFoundSubtitle')}
        footer={
          <a
            href="/login"
            onClick={(e) => { e.preventDefault(); onNavigate('/login'); }}
          >
            {t('join.backToSignIn')}
          </a>
        }
      >
        <div className={styles.errorCard}>
          <AlertCircle size={20} />
          <div className={styles.errorBody}>
            <p className={styles.errorTitle}>{t('join.invalidTitle')}</p>
            <p className={styles.errorHint}>
              {t('join.invalidHint')}
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Two-tier display: Location-tier codes show the building's name
  // as the headline + parent-operator name as the subtitle (e.g.
  // "Wilson Avenue · Wilson Operations Group"). Tenant-tier codes
  // show only the tenant's display name (the multifamily case has
  // no per-Location concept).
  const buildingName =
    building.kind === 'location'
      ? building.location.name
      : (building.tenant.display_name ?? building.tenant.name);
  const operatorSubtitle =
    building.kind === 'location'
      ? building.location.tenant_name ?? null
      : null;
  const addressLine =
    building.kind === 'location'
      ? `${building.location.address.street}, ${building.location.address.city}, ${building.location.address.state}`
      : null;

  // Authenticated path — show "Switch to {building}?" affordance
  // backed by the shared modal. The modal owns the join-building
  // call and the relogin handoff.
  if (isAuthenticated && currentUser && code) {
    return (
      <>
        <AuthLayout
          // Editorial: "Join {building}" not "Welcome to {building}".
          // Driver is the agent here, not a guest receiving hospitality.
          // Same call repeated on the unauthenticated branch below —
          // keep the two titles in sync if either changes.
          title={t('join.title', { building: buildingName })}
          subtitle={t('join.authedSubtitle')}
          footer={
            <a
              href="/profile"
              onClick={(e) => { e.preventDefault(); onNavigate('/profile'); }}
            >
              {t('join.stayHere')}
            </a>
          }
        >
          <div className={styles.tenantCard}>
            <div className={styles.tenantIcon} aria-hidden>
              <Building2 size={26} />
            </div>
            <div className={styles.tenantBody}>
              <p className={styles.tenantName}>{buildingName}</p>
              {operatorSubtitle && (
                <p className={styles.tenantHint}>{operatorSubtitle}</p>
              )}
              {addressLine && (
                <p className={styles.tenantHint}>{addressLine}</p>
              )}
              <p className={styles.tenantHint}>
                {t('join.codeLabel')} <code>{code}</code> · {t('join.validInvite')}
              </p>
            </div>
          </div>
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={() => setShowSwitchModal(true)}
          >
            {t('join.switchAccount')}
          </button>
        </AuthLayout>

        {showSwitchModal && (
          <SwitchBuildingModal
            initialCode={code}
            initialBuilding={building}
            currentBuilding={accountQ.data?.current_tenant
              ? {
                  name: accountQ.data.current_tenant.name,
                  isDefault: accountQ.data.current_tenant.is_default,
                }
              : null}
            onNavigate={onNavigate}
            onClose={() => setShowSwitchModal(false)}
          />
        )}
      </>
    );
  }

  // Unauthenticated path — fork to login or register, threading the
  // code through so the next-step screens know which building to
  // attach the user to. The server resolves Location vs Tenant
  // priority again at /auth/register time, so we just thread the
  // raw code.
  return (
    <AuthLayout
      // Editorial: see authed-branch note above — "Join" frames the
      // driver as the actor, not the recipient of a welcome.
      title={t('join.title', { building: buildingName })}
      subtitle={t('join.unauthSubtitle')}
    >
      <div className={styles.tenantCard}>
        <div className={styles.tenantIcon} aria-hidden>
          <Building2 size={26} />
        </div>
        <div className={styles.tenantBody}>
          <p className={styles.tenantName}>{buildingName}</p>
          {operatorSubtitle && (
            <p className={styles.tenantHint}>{operatorSubtitle}</p>
          )}
          {addressLine && (
            <p className={styles.tenantHint}>{addressLine}</p>
          )}
          <p className={styles.tenantHint}>
            {t('join.codeLabel')} <code>{code}</code>
          </p>
        </div>
      </div>

      <div className={styles.forkRow}>
        <button
          type="button"
          className={formStyles.btnPrimary}
          onClick={() => onNavigate(`/register?code=${encodeURIComponent(code!)}`)}
        >
          <UserPlus size={14} /> {t('join.createAccount')}
        </button>
        <button
          type="button"
          className={formStyles.btnSecondary}
          onClick={() => onNavigate(`/login?join_code=${encodeURIComponent(code!)}`)}
        >
          <LogIn size={14} /> {t('join.haveAccount')}
        </button>
      </div>
    </AuthLayout>
  );
}
